import { SUPABASE_URL, SUPABASE_ANON_KEY, SUPABASE_SERVICE_ROLE_KEY } from '../config/env';

// Helper function to get headers for API requests
const getAuthHeaders = () => {
  return {
    'apikey': SUPABASE_ANON_KEY,
    'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
    'Content-Type': 'application/json',
  };
};

const safeJsonParse = (value, fallback) => {
  try {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') {
      const s = value.trim();
      if (!s) return fallback;
      return JSON.parse(s);
    }
    // If already an object/array (e.g. json/jsonb), just return it
    return value;
  } catch {
    return fallback;
  }
};

const normalizeCallRow = (call) => {
  const normalized = { ...(call || {}) };
  // recordings is stored as JSON string in DB inserts; UI expects array
  normalized.recordings = Array.isArray(call?.recordings)
    ? call.recordings
    : safeJsonParse(call?.recordings, []);

  // aansh can be jsonb array (e.g. [28749]) but some inserts may store it as string
  normalized.aansh = Array.isArray(call?.aansh)
    ? call.aansh
    : safeJsonParse(call?.aansh, call?.aansh ?? null);

  // Normalize common casing differences so filters work regardless of schema
  if (normalized.callstatus === undefined && call?.callStatus !== undefined) normalized.callstatus = call.callStatus;
  if (normalized.callStatus === undefined && call?.callstatus !== undefined) normalized.callStatus = call.callstatus;
  if (normalized.ctype === undefined && call?.cType !== undefined) normalized.ctype = call.cType;
  if (normalized.cType === undefined && call?.ctype !== undefined) normalized.cType = call.ctype;

  return normalized;
};

const normalizeAgentId = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v).trim();
  return s.startsWith('#') ? s.slice(1) : s;
};

const extractAanshAgentIds = (aansh) => {
  if (!aansh) return [];
  if (Array.isArray(aansh)) return aansh;
  // Some sources might nest arrays/objects
  if (typeof aansh === 'object') {
    // If it's { ids: [...] } etc, try common patterns
    if (Array.isArray(aansh.ids)) return aansh.ids;
    return [];
  }
  // String JSON fallback
  const parsed = safeJsonParse(aansh, null);
  return Array.isArray(parsed) ? parsed : [];
};

// Convert a local YYYY-MM-DD into a UTC ISO boundary (avoids timezone off-by-one)
const localDateToUtcIsoBoundary = (dateStr, { endOfDay }) => {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = endOfDay
    ? new Date(y, m - 1, d, 23, 59, 59, 999)
    : new Date(y, m - 1, d, 0, 0, 0, 0);
  return dt.toISOString();
};

// Convert local YYYY-MM-DD + optional HH:mm into UTC ISO.
// If timeStr is missing/invalid, falls back to start/end-of-day based on endOfRange.
const localDateAndTimeToUtcIso = (dateStr, timeStr, { endOfRange }) => {
  if (!dateStr) return null;
  const [y, m, d] = String(dateStr).split('-').map(Number);
  if (!y || !m || !d) return null;

  const s = String(timeStr || '').trim();
  const parts = s ? s.split(':').map((x) => Number(x)) : [];
  const hh = Number.isFinite(parts[0]) ? parts[0] : null;
  const mm = Number.isFinite(parts[1]) ? parts[1] : null;

  if (hh === null || mm === null) {
    // No time provided -> boundary by day
    return localDateToUtcIsoBoundary(dateStr, { endOfDay: Boolean(endOfRange) });
  }

  // inclusive end: include whole minute
  const sec = endOfRange ? 59 : 0;
  const ms = endOfRange ? 999 : 0;
  const dt = new Date(y, m - 1, d, hh || 0, mm || 0, sec, ms);
  return dt.toISOString();
};

// Helper function to get service role headers (for webhook inserts - bypasses RLS)
const getServiceRoleHeaders = () => {
  return {
    'apikey': SUPABASE_SERVICE_ROLE_KEY,
    'Authorization': `Bearer ${SUPABASE_SERVICE_ROLE_KEY}`,
    'Content-Type': 'application/json',
  };
};

export const callReportService = {
  // Get call reports with filters
  async getCallReports(dateFilter = null, statusFilter = 'all', callTypeFilter = 'all', options = {}) {
    try {
      const agentCallReportIdRaw = options?.agentCallReportId;
      const agentCallReportId = normalizeAgentId(agentCallReportIdRaw);
      const headers = getAuthHeaders();
      
      // Apply date filter
      if (dateFilter && dateFilter.startDate && dateFilter.endDate) {
        const startDate = localDateAndTimeToUtcIso(dateFilter.startDate, dateFilter.startTime, { endOfRange: false });
        const endDate = localDateAndTimeToUtcIso(dateFilter.endDate, dateFilter.endTime, { endOfRange: true });
        
        const response = await fetch(
          `${SUPABASE_URL}/rest/v1/call_reports?select=*,service_requests:service_request_id(id,customer_name,vehicle_number,problem,complaint_status,contact_number)&createdat=gte.${startDate}&createdat=lte.${endDate}&order=createdat.desc`,
          {
            method: 'GET',
            headers: headers,
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch call reports');
        }

        let data = await response.json();
        data = (data || []).map(normalizeCallRow);

        // Apply status filter
        if (statusFilter !== 'all') {
          data = data.filter(call => {
            if (statusFilter === 'answered') {
              return [3, 5, 6, 10].includes(call.callstatus ?? call.callStatus);
            } else if (statusFilter === 'unanswered') {
              return [7, 8, 9].includes(call.callstatus ?? call.callStatus);
            } else if (statusFilter === 'failed') {
              return [13, 15, 16, 17, 18, 19].includes(call.callstatus ?? call.callStatus);
            }
            return true;
          });
        }

        // Apply call type filter
        if (callTypeFilter !== 'all') {
          data = data.filter(call => (call.ctype ?? call.cType) === callTypeFilter);
        }

        // Apply agent filter (match users.call_report_id against call_reports.aansh jsonb array)
        if (agentCallReportId) {
          data = data.filter((call) => {
            const ids = extractAanshAgentIds(call?.aansh).map(normalizeAgentId);
            return ids.includes(agentCallReportId);
          });
        }

        return { success: true, data: data, error: null };
      } else {
        // No date filter, get all
        const response = await fetch(
          `${SUPABASE_URL}/rest/v1/call_reports?select=*,service_requests:service_request_id(id,customer_name,vehicle_number,problem,complaint_status,contact_number)&order=createdat.desc&limit=100`,
          {
            method: 'GET',
            headers: headers,
          }
        );

        if (!response.ok) {
          throw new Error('Failed to fetch call reports');
        }

        let data = await response.json();
        data = (data || []).map(normalizeCallRow);

        // Apply filters
        if (statusFilter !== 'all') {
          data = data.filter(call => {
            if (statusFilter === 'answered') {
              return [3, 5, 6, 10].includes(call.callstatus ?? call.callStatus);
            } else if (statusFilter === 'unanswered') {
              return [7, 8, 9].includes(call.callstatus ?? call.callStatus);
            } else if (statusFilter === 'failed') {
              return [13, 15, 16, 17, 18, 19].includes(call.callstatus ?? call.callStatus);
            }
            return true;
          });
        }

        if (callTypeFilter !== 'all') {
          data = data.filter(call => (call.ctype ?? call.cType) === callTypeFilter);
        }

        if (agentCallReportId) {
          data = data.filter((call) => {
            const ids = extractAanshAgentIds(call?.aansh).map(normalizeAgentId);
            return ids.includes(agentCallReportId);
          });
        }

        return { success: true, data: data, error: null };
      }
    } catch (error) {
      console.error('Error fetching call reports:', error);
      return { success: false, data: [], error: error.message };
    }
  },

  // Helper function to clean phone number for matching
  cleanPhoneNumber(phoneNumber) {
    if (!phoneNumber) return null;
    // Remove all non-digit characters
    let cleaned = phoneNumber.replace(/\D/g, '');
    // Remove leading country codes like +91, 91, 0
    if (cleaned.startsWith('91') && cleaned.length > 10) {
      cleaned = cleaned.substring(2);
    } else if (cleaned.startsWith('0') && cleaned.length > 10) {
      cleaned = cleaned.substring(1);
    }
    // Ensure it's not longer than 10 digits
    return cleaned.substring(0, 10);
  },

  // Find matching service request by phone number
  async findMatchingServiceRequest(phoneNumber, useServiceRole = false) {
    try {
      if (!phoneNumber) return null;

      const cleanedPhone = this.cleanPhoneNumber(phoneNumber);
      if (!cleanedPhone || cleanedPhone.length < 10) return null;

      // Search in service_requests table - fetch recent records and match client-side
      // This is more reliable than ILIKE queries
      // Use service_role key if needed (for webhook context)
      const headers = useServiceRole ? getServiceRoleHeaders() : getAuthHeaders();
      const response = await fetch(
        `${SUPABASE_URL}/rest/v1/service_requests?select=id,contact_number,alternate_number&order=complaint_registered_at.desc&limit=1000`,
        {
          method: 'GET',
          headers: headers,
        }
      );

      if (!response.ok) {
        console.warn('Error searching for matching service request:', await response.text());
        return null;
      }

      const data = await response.json();
      if (data && data.length > 0) {
        // Find exact match after cleaning phone numbers
        for (const serviceRequest of data) {
          const srContact = this.cleanPhoneNumber(serviceRequest.contact_number);
          const srAlternate = this.cleanPhoneNumber(serviceRequest.alternate_number);
          
          if (srContact === cleanedPhone || srAlternate === cleanedPhone) {
            console.log(`✅ Matched phone ${cleanedPhone} to service request ${serviceRequest.id}`);
            return serviceRequest.id;
          }
        }
      }

      return null;
    } catch (error) {
      console.error('Error finding matching service request:', error);
      return null;
    }
  },

  // Save call report from webhook
  async saveCallReport(callData, useServiceRole = false) {
    try {
      // Validate required fields
      if (!callData.callId) {
        throw new Error('callId is required');
      }

      // Find matching service request by phone number
      let serviceRequestId = null;
      if (callData.cNumber) {
        serviceRequestId = await this.findMatchingServiceRequest(callData.cNumber, useServiceRole);
        if (serviceRequestId) {
          console.log(`✅ Matched call ${callData.callId} to service request ${serviceRequestId} by phone ${callData.cNumber}`);
        }
      }

      // Use service_role key if specified (for webhook)
      const authKey = useServiceRole ? SUPABASE_SERVICE_ROLE_KEY : SUPABASE_ANON_KEY;

      const response = await fetch(`${SUPABASE_URL}/rest/v1/call_reports`, {
        method: 'POST',
        headers: {
          'apikey': authKey,
          'Authorization': `Bearer ${authKey}`,
          'Content-Type': 'application/json',
          'Prefer': 'return=representation',
        },
        body: JSON.stringify({
          callId: callData.callId,
          callStatus: callData.callStatus || null,
          cType: callData.cType || null,
          DID: callData.did || callData.DID || null,
          campId: callData.campId || null,
          ivrSTime: callData.ivrSTime || null,
          ivrETime: callData.ivrETime || null,
          ivrDuration: callData.ivrDuration || null,
          userId: callData.userId || null,
          cNumber: callData.cNumber || callData.cNumber || null,
          masterNumCTC: callData.masterNumCTC || null,
          masterAgent: callData.masterAgent || null,
          masterAgentNumber: callData.masterAgentNumber || null,
          talkDuration: callData.talkDuration || null,
          agentOnCallDuration: callData.agentOnCallDuration || null,
          firstAttended: callData.firstAttended || null,
          firstAnswerTime: callData.firstAnswerTime || null,
          lastHangupTime: callData.lastHangupTime || null,
          custAnswerSTime: callData.custAnswerSTime || null,
          custAnswerETime: callData.custAnswerETime || null,
          custAnswerDuration: callData.custAnswerDuration || null,
          forward: callData.forward || false,
          totalHoldDuration: callData.totalHoldDuration || 0,
          aH: callData.aH ? JSON.stringify(callData.aH) : null,
          aAnsH: callData.aAnsH ? JSON.stringify(callData.aAnsH) : null,
          aHDetail: callData.aHDetail ? JSON.stringify(callData.aHDetail) : null,
          nH: callData.nH ? JSON.stringify(callData.nH) : null,
          nHDetail: callData.nHDetail ? JSON.stringify(callData.nHDetail) : null,
          recordings: callData.recordings ? JSON.stringify(callData.recordings) : null,
          cliArr: callData.cliArr ? JSON.stringify(callData.cliArr) : null,
          rawData: JSON.stringify(callData), // Store complete raw data
          serviceRequestId: serviceRequestId, // Link to service request if matched
          createdAt: new Date().toISOString(),
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Error saving call report:', errorText);
        throw new Error(`Failed to save call report: ${errorText}`);
      }

      const data = await response.json();
      return { success: true, data: data, error: null };
    } catch (error) {
      console.error('Error saving call report:', error);
      return { success: false, error: error.message };
    }
  },
};

